<template>
    <div class="thumbnail document-box">
        <div class="picture">
            <a :href="document.file.content" target="_blank">
                <img :src="document.file.preview"
                     alt="">
            </a>
            <div class="info f -a-center">
                <button class="btn btn-sm"
                        :class="{
                            'btn-default': !document.archived,
                            'btn-warning': document.archived
                        }"
                        @click.prevent="toggleArchive">
                    <i class="fa fa-archive"></i>
                </button>
                <div class="expire">
                    Exp&nbsp;:
                    <strong>
                        <editable-date :value.sync="document.expiration"
                                       :enable-reset="true"
                                       placeholder="pas d'expiration"></editable-date>
                    </strong>
                </div>
            </div>
            <div class="actions"
                 v-if="changed && !loading">
                <div class="btn-group">
                    <button class="btn btn-success btn-sm"
                            @click="validate"
                            :class="valid ? '' : 'disabled'">
                        <i class="glyphicon glyphicon-ok"></i>
                    </button>
                    <button class="btn btn-danger btn-sm"
                            @click="cancel">
                        <i class="glyphicon glyphicon-remove"></i>
                    </button>
                </div>
            </div>
            <div class="actions"
                 v-if="loading">
                <span class="btn btn-info btn-sm">
                    <loader></loader>
                </span>
            </div>
        </div>
        <div class="caption">
            <h5>
                <editable-text :value.sync="document.name"
                               placeholder="Pas de nom"></editable-text>
                &ndash;
                <em>
                    <editable-choice :value.sync="contactId"
                                     :enable-reset="true"
                                     placeholder="Pas de contact"
                                     :options="contactOptions"></editable-choice>
                </em>
            </h5>
            <p v-if="!(isProtocol || company)">
                <editable-choice :value.sync="docTypeId"
                                 :enable-reset="true"
                                 placeholder="Pas de type de document"
                                 :options="docTypeChoices"></editable-choice>
            </p>
            <p>
                <h5>
                    Status:
                    <editable-choice :value.sync="document.status"
                                        :enable-reset="true"
                                        placeholder="Status"
                                        :options="statusOptions"></editable-choice>
                </h5>
            </p>
            <p>
                <h5>
                    Types de documents:
                    <editable-choice :value.sync="document.type"
                                        :enable-reset="true"
                                        placeholder="Types de documents"
                                        :options="allDocTypeChoices"></editable-choice>
                </h5>
            </p>
        </div>
    </div>
</template>

<style lang="less"
       scoped>
    @import '~src/style/variables';

    .caption {
        h5 {
            font-weight: bold;
        }

        p {
            color: @gray;
            margin-bottom: 0;
        }
    }

    .picture {
        display: block;
        height: 300px;
        position: relative;

        img {
            width: 100%;
        }

        &::after {
            position: absolute;
            bottom: 0;

            content: " ";
            height: 50px;
            width: 100%;
            background-image:
                linear-gradient(to top, @thumbnail-bg, fadeout(@thumbnail-bg, 100%));
        }

         a {
             display: block;
             overflow: hidden;
             max-height: 100%;
         }
    }

    .info {
        position: absolute;
        z-index: 1;
        bottom: 10px;
        right: 10px;
    }

    .expire {
        margin-left: 5px;
        padding: 3px 8px;
        background-color: @brand-info;
        color: white;
        border-radius: 3px;
    }

    .actions {
        position: absolute;
        z-index: 1;
        top: -10px;
        right: -10px;
    }
</style>

<style>
    .document-box .editable-date .editable-item .placeholder {
        color: white;
    }
</style>

<script type="text/ecmascript-6">
    import EditableText from '../../components/editable/EditableText';
    import EditableChoice from '../../components/editable/EditableChoice';
    import EditableDate from '../../components/editable/EditableDate';
    import Loader from '../../components/Loader';
    import {getAllCompaniesMap} from '../../api/companies';
    import {reusableDocTypeChoices, allDocTypeChoices} from '../../api/document_types';
    import {d, asyncAction, asyncDebounce} from '../../utils';
    import equals from 'equals';
    import {update as updateDoc} from '../../api/documents';
    import {update as updateProtocol} from '../../api/protocols';
    import {manager as msgManager} from '../../messages';

    export default {
        components: {
            Loader,
            EditableDate,
            EditableChoice,
            EditableText
        },
        props: {
            document: {
                required: true
            },
            isProtocol: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                companiesMap: {},
                docTypeChoices: [],
                allDocTypeChoices: [],
                changed: false,
                backupDocument: undefined,
                loading: false,
                company: this.$parent.company
            };
        },
        computed: {
            contactOptions() {
                const t1 = d(() => this.$parent.$parent.client.contacts);
                const t2 = d(() => this.$parent.client.contacts);
                const t3 = d(() =>this.$parent.company.contacts);
                const contacts = t1 || t2 || t3;
                return contacts.map((contact) => ({
                    value: `${contact.id}`,
                    label: `${contact.first_name} ${contact.last_name}`
                }));
            },
            statusOptions() {
                return [{
                    value: 'valid',
                    label: 'Valid'
                },
                {
                    value: 'invalid',
                    label: 'Invalid'
                },
                {
                    value: 'todo',
                    label: 'TODO'
                }];
            },
            contactId: {
                get() {
                    return `${d(() => this.document.contact, '')}`;
                },
                set(v) {
                    if (this.document) {
                        this.document.contact = v ? parseInt(v, 10) : undefined;
                    }
                }
            },
            docTypeId: {
                get() {
                    return `${d(() => this.document.type, '')}`;
                },
                set(v) {
                    if (this.document) {
                        this.document.type = v ? parseInt(v, 10) : undefined;
                    }
                }
            },
            changed() {
                if (this.backupDocument) {
                    return !equals(this.document, this.backupDocument);
                }

                return false;
            },
            valid() {
                return true;
            }
        },
        watch: {
            docTypeId() {
                if (!this.document || this.document.name) {
                    return;
                }

                const dt = d(() => this
                    .docTypesDb
                    .filter((x) => `${x.id}` === this.docTypeId)[0]
                    .name
                );

                if (dt) {
                    this.document.name = dt;
                }
            }
        },
        methods: {
            backup() {
                this.backupDocument = Object.assign({}, this.document);
            },
            restore() {
                this.document = Object.assign({}, this.backupDocument);
            },
            cancel() {
                this.restore();
            },
            validate: asyncAction('loading', async function () {
                const update = this.isProtocol ? updateProtocol : updateDoc;
                await update(this.document);
                this.backup();
                msgManager.push(
                    'Succès.',
                    'Le document a bien été mis à jour',
                    'success'
                );
            }),
            toggleArchive: asyncDebounce(async function () {
                try {
                    this.document.archived = !this.document.archived;
                    await this.validate();
                } catch (e) {
                    console.log(e);
                }
            })
        },
        ready: async function ready() {
            this.backup();
            this.companiesMap = await getAllCompaniesMap();
            this.docTypeChoices = await reusableDocTypeChoices();
            this.allDocTypeChoices = await allDocTypeChoices();
        }
    };
</script>



// WEBPACK FOOTER //
// DocumentBox.vue?207f8369